// ── <TsdChart> — time-series-drawer chart body ──
//
// Extracted verbatim from AppInner's render (App.jsx ~line 6769) that rendered
// the TSD chart container, SVG path layers, fade overlays, today/gen/forecast
// lines, Y-axis label boxes, the hover tooltip, and the onMouseMove hit-detection
// handler. This is a behavior-preserving extraction: ALL logic is ported unchanged
// from App.jsx; only the closure values are re-sourced from props (`ctx`).
//
// The component is UNGATED — the caller (App.jsx) is responsible for
// rendering this component only when the TSD drawer is open/visible (preserving
// the exact gating and JSX position/z-order from AppInner).
//
// Module-level deps imported directly (same file used `import … from` in App.jsx):
//   C               — lib/colors.js  (mutable color object, assigned by theme)
//   R_START         — lib/timeline/geometry.js
//   Y_PAD           — lib/timeline/geometry.js
//   BRANCH_COLORS   — lib/timeline/geometry.js
//   clientToLocal   — lib/timeline/geometry.js (pointer → layout px, root CSS zoom divided out)
//
// Runtime values come from `ctx` (AppInner-scoped at render time):
//   tsdH            — chart height in px
//   tsdAnimating    — bool, drives height CSS transition
//   dragRef         — ref, for cursor style (grabbing / grab)
//   onMD            — mousedown handler (pan scroll)
//   screenToDay     — function: screen px → day number
//   tsdPaths        — memoized series path data (fill/CI/past/future/impact/branchPaths)
//   branches        — branch runs array (for hit-detection seriesImpacts walk)
//   series          — live series data object (used for unit lookup in hit-detection)
//   tsdAxes         — memoized left/right axis data {name, ticks, yv, color, unit, sourceUrl}
//   tsdHover        — hover state {x, y, value:{name,value,color,unit}, branchIndex} or null
//   setTsdHover     — setter for tsdHover
//   hoveredBranch   — current hovered branch ID (string | null)
//   setHoveredBranch — setter for hoveredBranch
//   todayX          — today's screen X in px
//   showTodayLine   — bool, whether to draw the today line (false once the forecast settled)
//   resScreenX      — resolution screen X in px (null or number)
//   fadeStartX      — fade start X in px (passed through to TimelineBackdrop)
//   fadeEndX        — fade end X in px (passed through to TimelineBackdrop)
//   showGenLine     — bool, whether to render the generated line
//   genX            — generated line screen X in px (null or number)
//   forecastingPhase — null | "awaiting" — drives vertGrowFromCenter animation
//   active          — active forecast run (for key derivation on forecast line)
//   chatOpen        — bool, right panel open (right-axis margin)
//   leftInset       — CSS length the left panel covers (left-axis margin; the rail layout)
//   vw              — viewport width in px

import { createPortal } from "react-dom";
import { ui } from "../../lib/telemetry/instrument.js";
import TimelineBackdrop from "./TimelineBackdrop.jsx";
import { themeColors } from "../../lib/colors.js";
import { R_START, Y_PAD, BRANCH_COLORS, clientToLocal } from "../../lib/timeline/geometry.js";
import { formatCompactTicks } from "../../lib/util/formatNumber.js";
import { surface } from "../../lib/telemetry/instrument.js";

export default function TsdChart({ ctx }) {
  const {
    tsdH,
    tsdAnimating,
    dragRef,
    onMD,
    screenToDay,
    tsdPaths,
    branches,
    series,
    tsdAxes,
    tsdHover,
    setTsdHover,
    hoveredBranch,
    setHoveredBranch,
    todayX,
    showTodayLine,
    resScreenX,
    fadeStartX,
    fadeEndX,
    showGenLine,
    genX,
    forecastingPhase,
    active,
    chatOpen,
    leftInset = 0,
    vw,
    theme,
  } = ctx;
  // This is an SVG chart: colors are applied as presentation attributes (fill/stroke)
  // and composed with hex-alpha, neither of which resolves a var() string, so read the
  // raw-hex palette for the current theme rather than the frozen var-based `C`.
  const C = themeColors(theme);

  // eslint-disable-next-line react-hooks/refs -- intentional drag-cursor read (verbatim from App.jsx; non-reactive cursor is fine)
  const dragCursor = dragRef.current ? "grabbing" : "grab";

  // Per-axis tick labels, formatted as a SET so the decimal precision adapts to
  // the range: a tight axis (e.g. world population 6.60B..6.70B) gets enough
  // decimals to stay distinct instead of collapsing to repeated "6.6B".
  const leftTickLabels = tsdAxes.left ? formatCompactTicks(tsdAxes.left.ticks) : [];
  const rightTickLabels = tsdAxes.right ? formatCompactTicks(tsdAxes.right.ticks) : [];

  return (
    <>
      {/* TSD CHART */}
      {/* telemetry-gesture: drag pans the time-series chart (view navigation) */}
      <div
        data-testid="tsd-chart"
        {...surface("tsd")}
        style={{
          height: tsdH,
          flexShrink: 0,
          overflow: "hidden",
          position: "relative",
          background: C.sectionBg,
          cursor: dragCursor,
          transition: tsdAnimating ? "height 0.28s ease" : "none",
        }}
        onMouseDown={onMD}
        onMouseMove={(e) => {
          // Layout px, with the root CSS zoom divided out — the paths are drawn in layout px.
          const { x: mx, y: my } = clientToLocal(e.currentTarget, e.clientX, e.clientY);
          const day = screenToDay(mx);
          // Hit-detection considers the base series AND every branch's adjusted line.
          // Branches take priority inside BRANCH_TOL — without that bias, a branch
          // line that runs close to the base series is unreachable because base
          // always wins on raw distance. Beyond BRANCH_TOL we fall back to whichever
          // line is closest. Tie-break with <= so a later branch (rendered on top)
          // wins if its Y matches an earlier one.
          const BRANCH_TOL = 22;
          // Branch RUN objects (active's store tree, same
          // order/index as the rendered branchPaths); seriesImpacts/forkDate 1:1, id → runId.
          const brs = branches;
          let bestBranch = null,
            bestBranchDist = Infinity;
          let bestBase = null,
            bestBaseDist = Infinity;
          for (const s of tsdPaths) {
            if (!s.data || !s.data.length) continue;
            const mn = s.yMin,
              mx2 = s.yMax;
            let closest = null,
              minDist = Infinity;
            for (const p of s.data) {
              const pd = (new Date(p.date) - R_START) / 86400000;
              const dist = Math.abs(pd - day);
              if (dist < minDist) {
                minDist = dist;
                closest = p;
              }
            }
            if (!closest || minDist > 180) continue;
            const baseY = 4 + (tsdH - 8) * (1 - (closest.value - mn) / (mx2 - mn || 1));
            const baseDist = Math.abs(my - baseY);
            if (baseDist < bestBaseDist) {
              bestBaseDist = baseDist;
              bestBase = {
                name: s.k,
                value: closest.value,
                color: s.color,
                unit: series[s.k]?.unit || "",
              };
            }
            for (let bi = 0; bi < brs.length; bi++) {
              const br = brs[bi];
              const bImpacts = br.seriesImpacts?.[s.k] || [];
              if (!bImpacts.length) continue;
              const forkD = br.forkDate ? new Date(br.forkDate) : null;
              if (forkD && new Date(closest.date) < forkD) continue;
              let delta = 0;
              for (const imp of bImpacts)
                if (new Date(imp.date) <= new Date(closest.date)) delta = imp.delta_percent;
              const av = closest.value * (1 + delta / 100);
              const branchY = 4 + (tsdH - 8) * (1 - (av - mn) / (mx2 - mn || 1));
              const branchDist = Math.abs(my - branchY);
              if (branchDist <= bestBranchDist) {
                bestBranchDist = branchDist;
                bestBranch = {
                  name: s.k,
                  value: av,
                  color: BRANCH_COLORS[bi % BRANCH_COLORS.length],
                  unit: series[s.k]?.unit || "",
                  branchId: br.runId,
                  branchIndex: bi,
                };
              }
            }
          }
          const overallTol = tsdH * 0.4;
          let pickedHover = null,
            pickedBranchId = null,
            pickedBranchIndex = null;
          if (bestBranch && bestBranchDist < BRANCH_TOL) {
            // Within tight branch tolerance — branch always wins, even if base is closer.
            pickedHover = {
              name: bestBranch.name,
              value: bestBranch.value,
              color: bestBranch.color,
              unit: bestBranch.unit,
            };
            pickedBranchId = bestBranch.branchId;
            pickedBranchIndex = bestBranch.branchIndex;
          } else if (
            bestBranch &&
            bestBranchDist < overallTol &&
            (bestBase == null || bestBranchDist <= bestBaseDist)
          ) {
            // Outside tight window, branch only wins if it's also the closest line overall.
            pickedHover = {
              name: bestBranch.name,
              value: bestBranch.value,
              color: bestBranch.color,
              unit: bestBranch.unit,
            };
            pickedBranchId = bestBranch.branchId;
            pickedBranchIndex = bestBranch.branchIndex;
          } else if (bestBase && bestBaseDist < overallTol) {
            pickedHover = bestBase;
          }
          setTsdHover(
            pickedHover
              ? { x: e.clientX, y: e.clientY, value: pickedHover, branchIndex: pickedBranchIndex }
              : null,
          );
          setHoveredBranch(pickedBranchId);
        }}
        onMouseLeave={() => {
          setTsdHover(null);
          setHoveredBranch(null);
        }}
      >
        <TimelineBackdrop
          todayX={todayX}
          vw={vw}
          fadeStartX={fadeStartX}
          fadeEndX={fadeEndX}
          theme={theme}
        />
        <svg width={vw} height={tsdH} style={{ display: "block", position: "relative", zIndex: 1 }}>
          <defs>
            {/* Combined series mask: fade-in at the data's left edge, fully opaque through the
              body, then fade out CENTERED on the forecast line. Single gradient covers the
              whole series so both past and future paths fade past the forecast line — fixes
              the case where a series's past-flagged points extend past today/forecast and
              therefore wouldn't fade under the old start/end-only mask. */}
            {tsdPaths.map((s) => {
              const sid = s.k.replace(/[^A-Za-z0-9_-]/g, "_");
              const TSD_FADE_W = 240;
              const FADE_IN_W = 80;
              const anchor = resScreenX != null ? resScreenX : fadeStartX;
              // Fade STARTS at the forecast line (was centered on it) so series stay fully
              // visible all the way to the forecast indicator, then taper off past it.
              const fadeStart = anchor;
              const fadeEnd = anchor + TSD_FADE_W;
              const gradStart = Math.max(0, Math.min(s.startX != null ? s.startX : 0, fadeEnd - 1));
              if (fadeEnd <= gradStart) return null;
              const span = fadeEnd - gradStart;
              const offFadeIn = Math.max(0, Math.min(1, FADE_IN_W / span));
              const offFadeStart = Math.max(offFadeIn, Math.min(1, (fadeStart - gradStart) / span));
              return (
                <linearGradient
                  key={"ffade-" + sid}
                  id={"ffade-" + sid}
                  x1={gradStart}
                  x2={fadeEnd}
                  y1="0"
                  y2="0"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stopColor="white" stopOpacity="0" />
                  <stop offset={offFadeIn} stopColor="white" stopOpacity="1" />
                  <stop offset={offFadeStart} stopColor="white" stopOpacity="1" />
                  <stop offset="1" stopColor="white" stopOpacity="0" />
                </linearGradient>
              );
            })}
            {tsdPaths.map((s) => {
              const sid = s.k.replace(/[^A-Za-z0-9_-]/g, "_");
              return (
                <mask key={"fmask-" + sid} id={"fmask-" + sid}>
                  <rect x={-50000} y="0" width={100000} height={tsdH} fill={`url(#ffade-${sid})`} />
                </mask>
              );
            })}
          </defs>
          {tsdPaths.map((s) => {
            const sid = s.k.replace(/[^A-Za-z0-9_-]/g, "_");
            return s.fill ? (
              <path
                key={s.k + "-f"}
                d={s.fill}
                fill={s.color}
                opacity=".07"
                mask={`url(#fmask-${sid})`}
              />
            ) : null;
          })}
          {tsdPaths.map((s) => {
            const sid = s.k.replace(/[^A-Za-z0-9_-]/g, "_");
            return s.confidenceFill ? (
              <path
                key={s.k + "-ci"}
                d={s.confidenceFill}
                fill={s.color}
                opacity=".05"
                mask={`url(#fmask-${sid})`}
              />
            ) : null;
          })}
          {tsdPaths.map((s) => {
            const sid = s.k.replace(/[^A-Za-z0-9_-]/g, "_");
            return s.pastPath ? (
              <path
                key={s.k + "-past"}
                d={s.pastPath}
                fill="none"
                stroke={s.color}
                strokeWidth="1.5"
                opacity=".9"
                mask={`url(#fmask-${sid})`}
              />
            ) : null;
          })}
          {tsdPaths.map((s) => {
            const sid = s.k.replace(/[^A-Za-z0-9_-]/g, "_");
            return s.futurePath ? (
              <path
                key={s.k + "-future"}
                d={s.futurePath}
                fill="none"
                stroke={s.color}
                strokeWidth="1.2"
                opacity=".25"
                mask={`url(#fmask-${sid})`}
              />
            ) : null;
          })}
          {tsdPaths.map((s) => {
            const sid = s.k.replace(/[^A-Za-z0-9_-]/g, "_");
            return s.impactPath ? (
              <path
                key={s.k + "-i"}
                d={s.impactPath}
                fill="none"
                stroke={theme === "dev" || theme === "dusk" ? C.gray : C.purple}
                strokeWidth="1.5"
                strokeDasharray="4,3"
                opacity=".2"
                mask={`url(#fmask-${sid})`}
              />
            ) : null;
          })}
          {/* Branch lines render visually only — hit-detection happens in the
            parent container's onMouseMove, which compares cursor Y to both
            the base series and each branch's adjusted Y. pointerEvents:none
            lets the cursor pass through to the underlying mouse handler
            regardless of stroke width, so hover doesn't require precision. */}
          {tsdPaths.flatMap((s) => {
            const sid = s.k.replace(/[^A-Za-z0-9_-]/g, "_");
            return (s.branchPaths || []).map((bp, bi) => {
              const isHov = hoveredBranch === bp.branchId;
              const isDim = hoveredBranch && !isHov;
              return (
                <path
                  key={s.k + "-br" + bi}
                  d={bp.path}
                  fill="none"
                  stroke={bp.color}
                  strokeWidth={isHov ? 2.2 : 1.2}
                  strokeDasharray="4,3"
                  opacity={isHov ? 0.9 : isDim ? 0.1 : 0.25}
                  mask={`url(#fmask-${sid})`}
                  style={{ transition: "opacity 0.2s, stroke-width 0.2s", pointerEvents: "none" }}
                />
              );
            });
          })}
        </svg>
        {/* Fade-out overlay past the forecast line — guarantees the time-series lines taper
          into the background instead of running off the chart at full opacity. The SVG mask
          above already attempts the same fade, but doing it again here as an HTML gradient
          makes it robust to mask-rendering quirks and easier to tune visually. */}
        {resScreenX !== null &&
          resScreenX < vw &&
          (() => {
            const FADE_W = 180;
            const overlayW = vw - resScreenX;
            if (overlayW <= 0) return null;
            const fadeEndPct = Math.min(100, (FADE_W / overlayW) * 100);
            return (
              <div
                style={{
                  position: "absolute",
                  left: resScreenX,
                  top: 0,
                  width: overlayW,
                  height: "100%",
                  background: `linear-gradient(to right, transparent 0%, ${C.future} ${fadeEndPct}%, ${C.future} 100%)`,
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              />
            );
          })()}
        {/* Today line through TSD */}
        {showTodayLine && todayX > -10 && todayX < vw + 10 && (
          <div
            style={{
              position: "absolute",
              left: todayX,
              top: 0,
              width: 2,
              height: "100%",
              background: `${C.today}44`,
              pointerEvents: "none",
            }}
          />
        )}
        {showGenLine && genX > -10 && genX < vw + 10 && (
          <div
            style={{
              position: "absolute",
              left: genX,
              top: 0,
              width: 2,
              height: "100%",
              background: `${C.gray}55`,
              pointerEvents: "none",
              transform: "translateX(-50%)",
            }}
          />
        )}
        {/* Forecast line through TSD */}
        {resScreenX !== null && resScreenX > -10 && resScreenX < vw + 10 && (
          <div
            key={`fline-tsd-${active?.id || ""}`}
            style={{
              position: "absolute",
              left: resScreenX,
              top: 0,
              width: 2,
              height: "100%",
              background: `${C.accent}44`,
              pointerEvents: "none",
              transformOrigin: "center",
              animation:
                forecastingPhase === "awaiting"
                  ? "vertGrowFromCenter 0.5s ease-out 0.5s both"
                  : "none",
            }}
          />
        )}
        {/* Left axis. Its gutter abuts a bordered UI element on the left, so the
          rotated axis-name label needs a bit more inset than the right axis to
          clear that border — otherwise it collides with / gets clipped by the
          edge. The right axis has no bordered neighbor on its side (it ends at
          an inner panel edge or the bare window edge), so its label sits fine
          without the extra inset. */}
        {tsdAxes.left && (
          <div
            // `rail-follow` drops the transition while the panel is being dragged, so the axis
            // tracks the edge exactly instead of trailing it.
            className="rail-follow"
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: Y_PAD,
              height: tsdH,
              background: C.bg,
              zIndex: 20,
              pointerEvents: "none",
              // Beside an open left panel, so the axis stays readable as it opens, closes or is
              // resized. A transform on the panel's own 0.3s ease, so the two move as one.
              transform: `translateX(${typeof leftInset === "number" ? `${leftInset}px` : leftInset})`,
              transition: "transform 0.3s ease",
              willChange: "transform",
            }}
          >
            <svg width={Y_PAD} height={tsdH}>
              {tsdAxes.left.ticks.map((v, i) => (
                <text
                  key={`${v}-${i}`}
                  x={Y_PAD - 4}
                  y={tsdAxes.left.yv(v) + 3}
                  fontSize="9"
                  fill={tsdAxes.left.color}
                  textAnchor="end"
                  fontFamily="IBM Plex Mono, monospace"
                >
                  {leftTickLabels[i]}
                </text>
              ))}
            </svg>
            <div
              style={{
                position: "absolute",
                left: 8,
                top: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                pointerEvents: tsdAxes.left.sourceUrl ? "auto" : "none",
              }}
            >
              <div
                {...ui("tsd.axis-source", "left")}
                onClick={
                  tsdAxes.left.sourceUrl
                    ? () => window.open(tsdAxes.left.sourceUrl, "_blank", "noopener,noreferrer")
                    : undefined
                }
                title={tsdAxes.left.sourceUrl ? `Open ${tsdAxes.left.name} source ↗` : undefined}
                style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                  fontSize: 14,
                  color: tsdAxes.left.color,
                  fontFamily: '"IBM Plex Mono",monospace',
                  fontWeight: "bold",
                  opacity: 0.7,
                  whiteSpace: "nowrap",
                  cursor: tsdAxes.left.sourceUrl ? "pointer" : "default",
                  userSelect: "none",
                }}
              >
                {tsdAxes.left.name}
              </div>
            </div>
          </div>
        )}
        {tsdAxes.right && (
          <div
            style={{
              position: "absolute",
              right: chatOpen ? "var(--chat-w, 264px)" : 0,
              top: 0,
              width: Y_PAD,
              height: tsdH,
              background: C.future,
              zIndex: 20,
              pointerEvents: "none",
              transition: "right 0.2s ease",
            }}
          >
            <svg width={Y_PAD} height={tsdH}>
              {tsdAxes.right.ticks.map((v, i) => (
                <text
                  key={`${v}-${i}`}
                  x={4}
                  y={tsdAxes.right.yv(v) + 3}
                  fontSize="9"
                  fill={tsdAxes.right.color}
                  textAnchor="start"
                  fontFamily="IBM Plex Mono, monospace"
                >
                  {rightTickLabels[i]}
                </text>
              ))}
            </svg>
            <div
              style={{
                position: "absolute",
                right: 2,
                top: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                pointerEvents: tsdAxes.right.sourceUrl ? "auto" : "none",
              }}
            >
              <div
                {...ui("tsd.axis-source", "right")}
                onClick={
                  tsdAxes.right.sourceUrl
                    ? () => window.open(tsdAxes.right.sourceUrl, "_blank", "noopener,noreferrer")
                    : undefined
                }
                title={tsdAxes.right.sourceUrl ? `Open ${tsdAxes.right.name} source ↗` : undefined}
                style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                  fontSize: 14,
                  color: tsdAxes.right.color,
                  fontFamily: '"IBM Plex Mono",monospace',
                  fontWeight: "bold",
                  opacity: 0.7,
                  whiteSpace: "nowrap",
                  cursor: tsdAxes.right.sourceUrl ? "pointer" : "default",
                  userSelect: "none",
                }}
              >
                {tsdAxes.right.name}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* TSD hover value tooltip — shows the value of whichever line the cursor
        is closest to (base series OR a branch). When a branch line wins, prefix
        with "Branch N · " so the user sees which scenario the number belongs to.
        PORTALED to document.body: the tooltip is `position: fixed` at viewport
        (clientX/clientY) coordinates, but an ancestor of the chart carries a CSS
        `transform` (the TSD-drawer lift, App.jsx ~5894). A non-`none` transform
        makes that ancestor the containing block for `position: fixed` descendants,
        so an in-tree tooltip would resolve its coords relative to that div (well
        down the page) instead of the viewport — landing far below the cursor.
        Rendering into <body> escapes the transform so `fixed` tracks the mouse. */}
      {tsdHover &&
        // telemetry-unsurfaced: a pointer-events-none tooltip, never attended.
        createPortal(
          <div
            style={{
              position: "fixed",
              left: tsdHover.x + 14,
              top: tsdHover.y - 10,
              zIndex: 500,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: tsdHover.value.color,
                fontFamily: '"IBM Plex Mono",monospace',
                whiteSpace: "nowrap",
                opacity: 0.9,
              }}
            >
              {tsdHover.branchIndex != null ? `Branch ${tsdHover.branchIndex + 1} · ` : ""}
              {tsdHover.value.name}:{" "}
              {tsdHover.value.unit === "USD" || tsdHover.value.unit?.startsWith("$") ? "$" : ""}
              {tsdHover.value.value >= 1000
                ? tsdHover.value.value.toLocaleString()
                : tsdHover.value.value.toFixed(2)}
              {tsdHover.value.unit &&
              !tsdHover.value.unit.startsWith("$") &&
              tsdHover.value.unit !== "USD" &&
              tsdHover.value.unit !== "pts"
                ? " " + tsdHover.value.unit
                : ""}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
